import { inject, PLATFORM_ID, REQUEST } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

const STORAGE_KEY: string = 'preferred-language';
const COOKIE_MAX_AGE_SECONDS: number = 60 * 60 * 24 * 365;

/**
 * Reads the visitor's previously selected language. On the client it comes from `localStorage`
 * (falling back to the cookie); during SSR it comes from the request cookie, so the server can
 * honor the preference too. Returns `null` when nothing is stored. Must be called within an
 * injection context.
 */
export function readStoredLanguage(): string | null {
    if (isPlatformBrowser(inject(PLATFORM_ID))) {
        return safeLocalStorage()?.getItem(STORAGE_KEY) ?? readCookie(inject(DOCUMENT).cookie);
    }

    const request: Request | null = inject(REQUEST, { optional: true });

    return readCookie(request?.headers.get('cookie') ?? '');
}

/**
 * Persists the selected language to `localStorage` (for the client) and a cookie (so the SSR
 * server can read it on the next request). Client-side only — safe to call from any context.
 */
export function writeStoredLanguage(language: string): void {
    safeLocalStorage()?.setItem(STORAGE_KEY, language);

    if (typeof document !== 'undefined') {
        document.cookie = `${STORAGE_KEY}=${language}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
    }
}

function safeLocalStorage(): Storage | null {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
}

function readCookie(cookieString: string): string | null {
    for (const part of cookieString.split(';')) {
        const [key, ...value] = part.trim().split('=');

        if (key === STORAGE_KEY) {
            return decodeURIComponent(value.join('=')) || null;
        }
    }

    return null;
}
